import { useEffect, useState } from 'react';
import { api, isErr, post, type Entity, type Meta } from './lib/api.ts';
import { Grid } from './views/Grid.tsx';
import { Login } from './views/Login.tsx';
import { Atlas } from './views/Atlas.tsx';
import { Org } from './views/Org.tsx';
import { Queue } from './views/Queue.tsx';
import { Manager } from './views/Manager.tsx';
import { EngineRoom } from './views/EngineRoom.tsx';
import { RowDrawer } from './components/RowDrawer.tsx';

/* The six screens, in their specified order. Every one is live now: an unported tab used to say what endpoint it
   was waiting on, because a tab that opens onto nothing is a dead control, and there is no longer
   anything to wait on. */
/* `mgr` marks a tab whose endpoints the server refuses to a rep. It is hidden rather than shown and
   refused: the persona dropdown in the old app decided which tabs to DRAW and nothing else, so the
   two halves could disagree. Here the server's rule is the rule and this list follows it — a tab a
   rep can click that answers 403 is a dead control, and hiding one whose data the server would hand
   over anyway adds nothing. Both halves are in MGR_ONLY in auth/index.ts. */
const TABS: [id: string, label: string, mgr?: boolean][] = [
  ['grid', 'Data grid'],
  ['atlas', 'Atlas'],
  ['org', 'Org charts'],
  ['queue', 'Work queue'],
  ['mgr', 'Manager', true],
  ['engine', 'Engine room', true],
];

const NUM = (v: unknown) => (v == null || v === '' ? '—' : Number(v).toLocaleString('en-US'));

interface Me { email: string; display_name: string | null; role: 'admin' | 'mgr' | 'rep' }

export default function App() {
  /* undefined = we have not asked yet, null = asked and not signed in. The distinction matters:
     rendering the login form while the session check is still in flight flashes a sign-in screen at
     someone who is already signed in on every reload. */
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<string>(() => location.hash.slice(1) || 'grid');

  /* Cross-screen hand-offs. Every one of these used to be a global mutated from four files; here
     they are state on the one component that owns the tab, so a hand-off cannot arrive at a screen
     that is not mounted. */
  const [drawer, setDrawer] = useState<{ entity: Entity; id: string | number } | null>(null);
  const [inject, setInject] = useState<{ ent: Entity; q: unknown } | null>(null);
  const [pin, setPin] = useState<{ f: string; o: string; v: string; label: string } | null>(null);

  const checkSession = () => api<{ user: Me }>('auth/me').then((r) => setMe(isErr(r) ? null : r.user));
  useEffect(() => { checkSession(); }, []);

  useEffect(() => {
    /* Only AFTER the session check passes. Fetching the registry first would fire a request the
       server answers 401 to and paint "Atlas did not load" over a perfectly working login. */
    if (!me) return;
    /* Everything the UI generates itself comes from here: 116 columns with their types, labels and
       provenance, 20 enum facets, the quick-filter names, the state list and the headline figures.
       One request, then the app can draw a filter for any column without a line of per-column code. */
    api<Meta>('meta').then((m) => (isErr(m) ? setErr(m.error) : setMeta(m)));
  }, [me]);

  useEffect(() => {
    const on = () => setTab(location.hash.slice(1) || 'grid');
    /* A hash change is a same-document navigation, so without this listener the view never
       re-renders and a screenshot of "the other tab" is a screenshot of this one. That trap cost
       a QA cycle before it was understood. */
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);

  const go = (t: string) => { location.hash = t; setTab(t); };

  if (me === undefined) return <div style={{ padding: 40, color: 'var(--dim)' }} />;
  if (me === null) return <Login onIn={checkSession} />;

  if (err) {
    return (
      <div style={{ padding: 40, maxWidth: 620 }}>
        <div className="t-label" style={{ color: 'var(--rd)' }}>Atlas did not load</div>
        <p style={{ marginTop: 10, color: 'var(--dim)', lineHeight: 1.6 }}>{err}</p>
        <p className="t-micro" style={{ marginTop: 14 }}>
          The API is the only thing this page talks to. If it is down, nothing here has a fallback —
          deliberately, because a screen that quietly falls back to stale data is worse than one that
          says it is broken.
        </p>
      </div>
    );
  }
  if (!meta) return <div style={{ padding: 40, color: 'var(--dim)' }}>Loading the column registry…</div>;

  const s = meta.stats || {};

  /* A gap on the Manager or Engine room page opens the exact rows behind it in the grid. That is
     what makes a count on those screens auditable rather than assertive. */
  const openGap = (ent: string, q: unknown) => { setInject({ ent: ent as Entity, q }); go('grid'); };
  const openPlant = (facId: number) => {
    setPin({ f: 'id', o: 'eq', v: String(facId), label: 'one plant sent over from the queue' });
    go('atlas');
  };

  return (
    <div className="atlas-flat" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 64px, not 48. This is the one screen carrying TWO identities, so it should not be the
          tightest bar in the product — the 30px lockup needs the room. */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 18,
        padding: '0 18px', height: 64, borderBottom: 'var(--hair)', flex: '0 0 auto',
      }}>
        {/* THEIR identity leads, then the product name subordinate to it behind a rule. "GTM Atlas"
            names the product, subordinate to their identity. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingRight: 17, borderRight: 'var(--hair)', flex: '0 0 auto' }}>
          <img src="/brand/GMR_logo_white20text_orange20logo_horizontal.svg" alt="GrayMatter Robotics"
            style={{ height: 30, width: 'auto', display: 'block' }} />
          <div style={{ fontWeight: 600, letterSpacing: '.02em', fontSize: 16, lineHeight: 1, paddingLeft: 15, borderLeft: '1px solid var(--line2)' }}>
            GTM&nbsp;<span style={{ color: 'var(--or)' }}>Atlas</span>
          </div>
        </div>

        {/* THE BACKSTOP. Three times on the old bar a change to something in here — a settings gear,
            a mono font, a bigger logo — pushed the build credit off the right edge of the page, and
            each time it was only caught by re-measuring. `minWidth: 0` plus a scrolling nav makes
            the overflow structurally impossible: the tabs give up width before the page does.
            If anything is ever added to this bar, check the page does not scroll sideways at
            1280px — that is the width it failed at every time. */}
        <nav style={{ display: 'flex', gap: 2, minWidth: 0, flex: '0 1 auto', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {TABS.filter(([, , mgr]) => !mgr || me.role !== 'rep').map(([id, label]) => {
            const on = tab === id;
            return (
              <a
                key={id}
                href={`#${id}`}
                style={{
                  position: 'relative', padding: '0 12px', height: 64, display: 'flex', alignItems: 'center',
                  whiteSpace: 'nowrap',
                  fontSize: 'var(--t-body)', textDecoration: 'none',
                  /* Active state is white text + a 2px accent rule + a faint tint — never a solid
                     orange block. Criterion E1: orange appears on data and on the active rule, and
                     nowhere else. */
                  color: on ? '#FFF' : 'var(--dim)',
                  background: on ? 'rgba(255,170,0,.043)' : 'transparent',
                  // A border, not a box-shadow — the Atlas reset strips shadows. See ui.tsx Btn.
                  borderBottom: on ? '2px solid var(--or)' : '2px solid transparent',
                }}
              >{label}</a>
            );
          })}
        </nav>

        {/* Headline figures come from `meta`. Nothing on this bar is typed: the Vision tab once had
            hardcoded numbers contradicting the header on the same screen. */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 22, alignItems: 'baseline' }}>
          {([['accounts', s.accounts], ['plants', s.plants], ['contacts', s.people ?? s.contacts]] as const).map(([k, v]) => (
            <span key={k} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span className="num" style={{ color: 'var(--am)', fontSize: 13 }}>{NUM(v)}</span>
              <span className="t-label">{k}</span>
            </span>
          ))}
          {/* Who is signed in, and the way out. A session with no visible way to end it is the same
              class of trap as a pane you can close and cannot reopen. */}
          <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', borderLeft: 'var(--hair)', paddingLeft: 22 }}>
            <span className="t-micro" style={{ color: 'var(--dim)' }} title={`${me.email} · ${me.role}`}>
              {me.display_name || me.email}
            </span>
            <button
              onClick={async () => { await post('auth/logout'); setMe(null); setMeta(null); }}
              className="t-label"
              style={{ border: 'none', background: 'none', color: 'var(--dim2)', cursor: 'pointer', padding: 0 }}
            >sign out</button>
          </span>

          {/* The build credit, the way an agency signs work. Growth Duck yellow appears HERE AND
              NOWHERE ELSE in the product — this is the one element on screen that is ours, and
              using the colour anywhere else would put two brands in competition on a client's app.
              It also sits in the same warm family as their orange, so the bar reads as one palette
              rather than two identities fighting. */}
          {/* Not a link. The old app pointed it at #glossary, i.e. nowhere off this origin, and the
              image build greps the bundle for any external hostname and fails on a hit — a credit
              is not worth weakening that check for. */}
          <span
            title="Built for GrayMatter Robotics by Growth Duck"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 15,
              borderLeft: 'var(--hair)', opacity: 0.8,
              color: 'var(--dim2)', fontSize: 11, lineHeight: 1.25, flex: '0 0 auto',
            }}
          >
            <img src="/brand/growthduck-mark.png" alt="Growth Duck" style={{ height: 26, width: 'auto', display: 'block' }} />
            <span>built by <b style={{ color: 'var(--dim)', fontWeight: 600 }}>Growth&nbsp;Duck</b></span>
          </span>
        </div>
      </header>

      <main style={{ flex: '1 1 auto', minHeight: 0 }}>
        {tab === 'grid' && (
          <Grid
            meta={meta}
            inject={inject}
            onInjected={() => setInject(null)}
            onOpenRow={(entity, id) => setDrawer({ entity, id })}
            onOpenInAtlas={(p) => { setPin(p); go('atlas'); }}
          />
        )}
        {tab === 'atlas' && (
          <Atlas
            meta={meta}
            pin={pin}
            onClearPin={() => setPin(null)}
            onOpenPerson={(id) => setDrawer({ entity: 'ppl', id })}
          />
        )}
        {tab === 'org' && <Org onOpenPerson={(id) => setDrawer({ entity: 'ppl', id })} />}
        {tab === 'queue' && (
          <Queue
            onOpenPerson={(id) => setDrawer({ entity: 'ppl', id })}
            onOpenPlant={openPlant}
          />
        )}
        {tab === 'mgr' && <Manager onOpenGap={openGap} />}
        {tab === 'engine' && <EngineRoom meta={meta} onOpenGap={openGap} />}
      </main>

      {drawer && (
        <RowDrawer
          meta={meta} entity={drawer.entity} id={drawer.id}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}
